import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Quote, Star, ArrowLeft, ArrowRight } from 'lucide-react';
const testimonials = [{
  quote: 'Investing with Zylus was the best decision I made this year. The transparency and professionalism are unmatched in the industry. Every promise was delivered.',
  name: 'Oluwaseun Adebayo',
  role: 'Property Investor',
  location: 'Lekki, Lagos',
  rating: 5,
  avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
  property: 'Zylus Court Phase II'
}, {
  quote: 'From the initial inspection to handing over the keys, the process was seamless. They truly understand luxury and comfort. My family loves our new home.',
  name: 'Chioma Nwosu',
  role: 'Homeowner',
  location: 'Ibeju-Lekki',
  rating: 5,
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
  property: 'Tiwa Garden City'
}, {
  quote: 'Their commitment to sustainable building and long-term client relationships is evident in every interaction. Highly recommended for serious investors.',
  name: 'David Olatunji',
  role: 'Corporate Client',
  location: 'Ikoyi, Lagos',
  rating: 5,
  avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
  property: 'Park Lane Phase II'
}, {
  quote: 'The flexibility in payment plans made owning my dream home a reality. The Zylus team walked with me every step of the way. Truly a class apart.',
  name: 'Amina Yusuf',
  role: 'First-time Buyer',
  location: 'Abraham Adesanya',
  rating: 5,
  avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80',
  property: 'Lekki Pride III'
}];
export function Testimonials() {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  useEffect(() => {
    if (isPaused) return;
    const timer = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % testimonials.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [isPaused]);
  const next = () => setActiveIndex(prev => (prev + 1) % testimonials.length);
  const prev = () => setActiveIndex(prev => (prev - 1 + testimonials.length) % testimonials.length);
  const active = testimonials[activeIndex];
  return <section className="relative py-24 md:py-32 px-6 md:px-12 bg-bone overflow-hidden" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-678">
      {/* Decorative giant quote mark */}
      <div className="absolute top-12 right-12 text-gold/[0.06] pointer-events-none select-none" data-id="element-679">
        <Quote className="w-[400px] h-[400px]" strokeWidth={1} data-id="element-680" />
      </div>

      <div className="relative max-w-[1280px] mx-auto" data-id="element-681">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16" data-id="element-682">
          <div data-id="element-683">
            <motion.div initial={{
            opacity: 0,
            y: 10
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-xs font-semibold tracking-[0.2em] uppercase text-gold mb-4" data-id="element-684">
              Client Stories
            </motion.div>
            <motion.h2 initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="text-4xl md:text-5xl font-serif text-ink tracking-tight max-w-xl leading-[1.1]" data-id="element-685">
              Loved by 3,000+ homeowners across Nigeria.
            </motion.h2>
          </div>

          {/* Nav controls */}
          <div className="flex items-center gap-3" data-id="element-686">
            <button onClick={prev} aria-label="Previous testimonial" className="w-12 h-12 rounded-full border border-ink/10 flex items-center justify-center hover:bg-ink hover:text-white hover:border-ink transition-all group" data-id="element-687">
              <ArrowLeft className="w-4 h-4" data-id="element-688" />
            </button>
            <button onClick={next} aria-label="Next testimonial" className="w-12 h-12 rounded-full bg-ink text-white flex items-center justify-center hover:bg-gold transition-colors" data-id="element-689">
              <ArrowRight className="w-4 h-4" data-id="element-690" />
            </button>
          </div>
        </div>

        {/* Main grid: featured card + side stack */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6" data-id="element-691">
          {/* Featured Card */}
          <div className="lg:col-span-8" data-id="element-692">
            <AnimatePresence mode="wait" data-id="element-693">
              <motion.div key={activeIndex} initial={{
              opacity: 0,
              y: 30,
              scale: 0.98
            }} animate={{
              opacity: 1,
              y: 0,
              scale: 1
            }} exit={{
              opacity: 0,
              y: -20,
              scale: 0.98
            }} transition={{
              duration: 0.6,
              ease: [0.16, 1, 0.3, 1]
            }} className="relative bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-ink/5 min-h-[420px] flex flex-col justify-between overflow-hidden" data-id="element-694">
                {/* Gold accent stripe */}
                <div className="absolute top-0 left-0 w-1 h-full bg-gold" data-id="element-695" />

                {/* Quote icon */}
                <div className="absolute top-8 right-8 text-gold/20" data-id="element-696">
                  <Quote className="w-12 h-12" strokeWidth={1.5} fill="currentColor" data-id="element-697" />
                </div>

                {/* Stars */}
                <div className="flex items-center gap-1 mb-6" data-id="element-698">
                  {Array.from({
                  length: active.rating
                }).map((_, i) => <Star key={i} className="w-4 h-4 fill-gold text-gold" data-id="element-699" />)}
                </div>

                {/* Quote */}
                <p className="text-xl md:text-3xl font-serif text-ink leading-snug mb-8 max-w-2xl" data-id="element-700">
                  "{active.quote}"
                </p>

                {/* Footer: avatar + meta */}
                <div className="flex flex-wrap items-center justify-between gap-6 pt-6 border-t border-ink/5" data-id="element-701">
                  <div className="flex items-center gap-4" data-id="element-702">
                    <img src={active.avatar} alt={active.name} className="w-14 h-14 rounded-full object-cover ring-2 ring-gold/20" data-id="element-703" />
                    <div data-id="element-704">
                      <div className="font-semibold text-ink" data-id="element-705">
                        {active.name}
                      </div>
                      <div className="text-sm text-ink/60" data-id="element-706">
                        {active.role} · {active.location}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-bone-dark" data-id="element-707">
                    <span className="w-2 h-2 rounded-full bg-gold" data-id="element-708" />
                    <span className="text-xs font-semibold tracking-wide text-ink/70" data-id="element-709">
                      Owner of {active.property}
                    </span>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Progress + indicator row */}
            <div className="mt-6 flex items-center gap-6" data-id="element-710">
              <span className="text-sm font-mono text-ink/40 tabular-nums" data-id="element-711">
                {String(activeIndex + 1).padStart(2, '0')} /{' '}
                {String(testimonials.length).padStart(2, '0')}
              </span>
              <div className="flex-1 h-[1px] bg-ink/10 relative overflow-hidden" data-id="element-712">
                <motion.div key={`progress-${activeIndex}-${isPaused}`} className="absolute top-0 left-0 h-full bg-gold" initial={{
                width: '0%'
              }} animate={{
                width: isPaused ? '0%' : '100%'
              }} transition={{
                duration: isPaused ? 0 : 6,
                ease: 'linear'
              }} data-id="element-713" />
              </div>
            </div>
          </div>

          {/* Side stack */}
          <div className="lg:col-span-4 flex flex-col gap-4" data-id="element-714">
            {testimonials.map((t, i) => {
            if (i === activeIndex) return null;
            return <motion.button key={i} onClick={() => setActiveIndex(i)} initial={{
              opacity: 0,
              x: 20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              delay: i * 0.05
            }} whileHover={{
              y: -4
            }} className="text-left bg-white rounded-2xl p-5 border border-ink/5 hover:border-gold/40 transition-colors group flex-1" data-id="element-715">
                  <div className="flex items-center gap-3 mb-3" data-id="element-716">
                    <img src={t.avatar} alt={t.name} className="w-10 h-10 rounded-full object-cover" data-id="element-717" />
                    <div className="flex-1 min-w-0" data-id="element-718">
                      <div className="font-semibold text-ink text-sm truncate" data-id="element-719">
                        {t.name}
                      </div>
                      <div className="text-xs text-ink/50 truncate" data-id="element-720">
                        {t.role}
                      </div>
                    </div>
                    <div className="flex items-center gap-0.5" data-id="element-721">
                      {Array.from({
                    length: t.rating
                  }).map((_, idx) => <Star key={idx} className="w-3 h-3 fill-gold text-gold" data-id="element-722" />)}
                    </div>
                  </div>
                  <p className="text-sm text-ink/70 line-clamp-3 leading-relaxed" data-id="element-723">
                    "{t.quote}"
                  </p>
                  <div className="mt-3 text-xs text-gold font-medium opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-724">
                    Read more →
                  </div>
                </motion.button>;
          })}
          </div>
        </div>
      </div>
    </section>;
}